import { useCallback, useEffect, useMemo, useRef, useState, useId, memo, type ReactNode, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import '@/styles/snap.css'
import { HelpCircle, Minus, Maximize2, Minimize2, X as CloseIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  TASKBAR_HEIGHT,
  DEFAULT_WINDOW_WIDTH,
  DEFAULT_WINDOW_HEIGHT,
  MIN_WINDOW_WIDTH,
  MIN_WINDOW_HEIGHT,
  calculateResponsiveWindowSize,
  calculateCenteredWindowPosition,
} from '@/shared/ui'
import { useAdaptiveTransition } from '@/hooks/useAdaptiveTransition'
import { getWindowAnimationPreset, type WindowAnimationContext, type WindowAnimationPreset } from '@/utils/windowAnimations'
import { taskbarButtonRegistry } from '@/services/taskbar-button-registry'
import { shouldClaimWindowFocus, shouldFocusWindowOnMouseDown, shouldRaiseWindowOnFocus } from '@/utils/windowFocus'
import { useTranslation } from 'react-i18next'

type Coordinates = {
  x: number
  y: number
}

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

type ResizeState = {
  startX: number
  startY: number
  origW: number
  origH: number
  origX: number
  origY: number
  direction: ResizeDirection
  snapSide: 'left' | 'right' | null
}

type PositionChangeOptions = {
  fromUser?: boolean
  size?: { w: number; h: number }
  snap?: 'left' | 'right' | null
  isMaximized?: boolean
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min
  return Math.min(Math.max(value, min), max)
}
const RESIZE_HANDLES: Array<{
  direction: ResizeDirection
  className: string
  labelKey: string
}> = [
  { direction: 'e', className: 'resize-handle--e', labelKey: 'window.resize.right' },
  { direction: 'w', className: 'resize-handle--w', labelKey: 'window.resize.left' },
  { direction: 's', className: 'resize-handle--s', labelKey: 'window.resize.bottom' },
  { direction: 'n', className: 'resize-handle--n', labelKey: 'window.resize.top' },
  { direction: 'se', className: 'resize-handle--se', labelKey: 'window.resize.bottomRight' },
  { direction: 'sw', className: 'resize-handle--sw', labelKey: 'window.resize.bottomLeft' },
  { direction: 'ne', className: 'resize-handle--ne', labelKey: 'window.resize.topRight' },
  { direction: 'nw', className: 'resize-handle--nw', labelKey: 'window.resize.topLeft' },
]

type AppWindowProps = {
  title: string
  children: ReactNode
  className?: string
  allowMinimize?: boolean
  allowClose?: boolean
  onClose?: () => void
  /**
   * Darf jetzt geschlossen werden? `false` bedeutet: Das Schliessen wurde
   * abgelehnt (eine Rueckfrage laeuft) — dann darf die Exit-Animation gar
   * nicht erst starten, sonst bleibt ein unsichtbares Fenster zurueck.
   */
  onCloseRequest?: () => boolean
  minimized?: boolean
  onToggleMinimize?: () => void
  onFocus?: () => void
  allowMaximize?: boolean
  icon?: ReactNode
  initialPosition?: Coordinates
  position?: Coordinates
  size?: { w: number; h: number }
  isMaximized?: boolean
  snapSide?: 'left' | 'right' | null
  onPositionChange?: (position: Coordinates, options?: PositionChangeOptions) => void
  appId?: string
  isActive?: boolean
  animationContext?: WindowAnimationContext
  zIndex?: number
}

/**
 * Ein einfaches App-Fenster im cdb-WebOS Stil mit interner useState-Steuerung.
 * - oeffnen/schliessen (ESC oder schliessen-Button)
 * - Minimieren (Titelzeile bleibt sichtbar)
 */
function AppWindow({
  title,
  children,
  className,
  allowMinimize = true,
  allowClose = true,
  onClose,
  onCloseRequest,
  minimized,
  onToggleMinimize,
  onFocus,
  allowMaximize = true,
  icon,
  initialPosition,
  position: controlledPosition,
  size: controlledSize,
  isMaximized: controlledIsMaximized = false,
  snapSide: controlledSnapSide = null,
  onPositionChange,
  appId,
  isActive = false,
  animationContext = 'system',
  zIndex = 1000,
}: AppWindowProps) {
  const { t } = useTranslation()
  const { getTiming } = useAdaptiveTransition()
  const animationPreset = useMemo(() => getWindowAnimationPreset(animationContext), [animationContext])
  const buildTiming = useCallback(
    (
      preset: WindowAnimationPreset['enter'] | WindowAnimationPreset['exit'],
      fallbackPhase: 'enter' | 'exit',
    ) =>
      getTiming(preset.timingPreset, {
        context: 'window',
        phase: fallbackPhase,
        ...preset.timingOverrides,
      }),
    [getTiming],
  )
  const windowEnterTiming = useMemo(
    () => buildTiming(animationPreset.enter, 'enter'),
    [animationPreset.enter, buildTiming],
  )
  const windowExitTiming = useMemo(
    () => buildTiming(animationPreset.exit, 'exit'),
    [animationPreset.exit, buildTiming],
  )
  const windowCloseTiming = windowExitTiming
  const snapOverlayTiming = getTiming('quick', {
    context: 'window',
    phase: 'enter',
    clamp: { min: 120, max: 260 },
  })
  const [minState, setMinState] = useState<boolean>(false)
  const [pos, setPos] = useState<Coordinates>(createInitialPosition)
  const [size, setSize] = useState<{ w: number; h: number }>(createDefaultSize)
  const [maximized, setMaximized] = useState<boolean>(controlledIsMaximized)
  const [snapPreview, setSnapPreview] = useState<null | 'left' | 'right'>(null)
  const [snappedSide, setSnappedSide] = useState<'left' | 'right' | null>(controlledSnapSide)
  const [isClosing, setIsClosing] = useState(false)
  const winRef = useRef<HTMLDivElement | null>(null)
  const posRef = useRef<Coordinates>(pos)
  const sizeRef = useRef<{ w: number; h: number }>(size)
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)
  const resizeRef = useRef<ResizeState | null>(null)
  const prevRectRef = useRef<{ pos: Coordinates; size: { w: number; h: number } } | null>(null)
  const restoredOnDragRef = useRef(false)
  const snappedSideRef = useRef<'left' | 'right' | null>(controlledSnapSide)
  const viewportRef = useRef<{ width: number; height: number }>({
    width: typeof window !== 'undefined' ? window.innerWidth : DEFAULT_WINDOW_WIDTH,
    height: typeof window !== 'undefined' ? window.innerHeight : DEFAULT_WINDOW_HEIGHT + TASKBAR_HEIGHT,
  })
  const titleId = useId()
  const [isMounted, setIsMounted] = useState(false)
  const closingTimeoutRef = useRef<number | null>(null)
  const closingRef = useRef(false)
  const [renderedSnapSide, setRenderedSnapSide] = useState<null | 'left' | 'right'>(null)
  const [snapOverlayVisible, setSnapOverlayVisible] = useState(false)
  const snapPreviewRef = useRef<null | 'left' | 'right'>(null)
  const deferredMaximizedRef = useRef(false)
  const [isRestoring, setIsRestoring] = useState(false)
  const restoringTimeoutRef = useRef<number | null>(null)
  const geometryTransitionRef = useRef<number | null>(null)
  const animationStyle = useMemo(() => {
    const style: CSSProperties = {
      '--window-enter-translate-x': animationPreset.enter.translateX ?? '0px',
      '--window-enter-translate-y': animationPreset.enter.translateY ?? '18px',
      '--window-enter-scale': (animationPreset.enter.scale ?? 0.95).toString(),
      '--window-enter-opacity': (animationPreset.enter.opacity ?? 0).toString(),
      '--window-enter-blur': animationPreset.enter.blur ?? '0.35px',
      '--window-exit-translate-x': animationPreset.exit.translateX ?? '0px',
      '--window-exit-translate-y': animationPreset.exit.translateY ?? '16px',
      '--window-exit-scale': (animationPreset.exit.scale ?? 0.94).toString(),
      '--window-exit-opacity': (animationPreset.exit.opacity ?? 0).toString(),
      '--window-exit-blur': animationPreset.exit.blur ?? '0.2px',
      '--window-motion-duration': `${windowEnterTiming.duration}ms`,
      '--window-motion-ease': windowEnterTiming.easing,
      '--window-exit-duration': `${windowExitTiming.duration}ms`,
      '--window-exit-ease': windowExitTiming.easing,
    } as CSSProperties
    return style
  }, [animationPreset, windowEnterTiming.duration, windowEnterTiming.easing, windowExitTiming.duration, windowExitTiming.easing])

  const requestClose = useCallback(() => {
    if (!allowClose) return
    if (closingRef.current) return
    // Erst fragen, dann animieren (#850). Ein Schliessen kann abgelehnt werden
    // — etwa wenn das Fenster ungespeicherte Aenderungen meldet. Die Animation
    // blendet das Fenster aus und schaltet Klicks ab, und `closingRef` rastet
    // dauerhaft ein; liefe sie bei einem abgelehnten Schliessen mit, bliebe das
    // Fenster unsichtbar, unbedienbar und nicht mehr schliessbar zurueck.
    if (onCloseRequest && !onCloseRequest()) return
    closingRef.current = true
    setIsClosing(true)

    if (typeof window !== 'undefined') {
      closingTimeoutRef.current = window.setTimeout(() => {
        onClose?.()
      }, windowCloseTiming.duration)
    } else {
      onClose?.()
    }
  }, [allowClose, onClose, onCloseRequest, windowCloseTiming.duration])

  function createInitialPosition(): Coordinates {
    if (initialPosition) {
      return { ...initialPosition }
    }
    const defaultSize = createDefaultSize()
    return calculateCenteredWindowPosition(defaultSize.w, defaultSize.h)
  }

  function createDefaultSize(): { w: number; h: number } {
    if (appId) {
      return calculateResponsiveWindowSize(appId)
    }
    return { w: DEFAULT_WINDOW_WIDTH, h: DEFAULT_WINDOW_HEIGHT }
  }

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined') {
        if (closingTimeoutRef.current !== null) window.clearTimeout(closingTimeoutRef.current)
        if (geometryTransitionRef.current !== null) window.clearTimeout(geometryTransitionRef.current)
      }
    }
  }, [])

  useEffect(() => {
    snappedSideRef.current = snappedSide
  }, [snappedSide])

  useEffect(() => {
    if (controlledSnapSide === undefined) return
    if (controlledSnapSide === snappedSideRef.current) return
    setSnappedSide(controlledSnapSide)
  }, [controlledSnapSide])

  useEffect(() => {
    if (!controlledPosition) return
    if (dragRef.current || resizeRef.current) return
    if (posRef.current.x === controlledPosition.x && posRef.current.y === controlledPosition.y) return
    posRef.current = { ...controlledPosition }
    setPos(controlledPosition)
  }, [controlledPosition?.x, controlledPosition?.y])

  useEffect(() => {
    if (!controlledSize) return
    if (dragRef.current || resizeRef.current) return
    if (sizeRef.current.w === controlledSize.w && sizeRef.current.h === controlledSize.h) return
    sizeRef.current = { ...controlledSize }
    setSize(controlledSize)
  }, [controlledSize?.w, controlledSize?.h])

  useEffect(() => {
    setMaximized(controlledIsMaximized)
  }, [controlledIsMaximized])

  useEffect(() => {
    if (typeof window === 'undefined') return
    viewportRef.current = {
      width: window.innerWidth,
      height: window.innerHeight,
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsMounted(true)
      return
    }
    const frame = window.requestAnimationFrame(() => setIsMounted(true))
    return () => window.cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    let frame: number | undefined
    let timeout: number | undefined

    if (snapPreview) {
      setRenderedSnapSide(snapPreview)
      if (typeof window !== 'undefined') {
        frame = window.requestAnimationFrame(() => setSnapOverlayVisible(true))
      } else {
        setSnapOverlayVisible(true)
      }
    } else if (renderedSnapSide) {
      setSnapOverlayVisible(false)
      if (typeof window !== 'undefined') {
        timeout = window.setTimeout(() => {
          setRenderedSnapSide(null)
        }, snapOverlayTiming.duration)
      } else {
        setRenderedSnapSide(null)
      }
    }

    return () => {
      if (typeof window !== 'undefined') {
        if (frame !== undefined) window.cancelAnimationFrame(frame)
        if (timeout !== undefined) window.clearTimeout(timeout)
      }
    }
  }, [snapPreview, renderedSnapSide, snapOverlayTiming.duration])

  const isMin = typeof minimized === 'boolean' ? minimized : minState
  const prevMinimizedRef = useRef(isMin)

  // Minimize-to-Taskbar / Restore-from-Taskbar animation
  useEffect(() => {
    const wasMin = prevMinimizedRef.current
    prevMinimizedRef.current = isMin

    if (!wasMin && isMin) {
      // MINIMIZE: compute target position toward the taskbar button
      const el = winRef.current
      if (el && appId) {
        const buttonRect = taskbarButtonRegistry.measure(appId)
        if (buttonRect) {
          const windowRect = el.getBoundingClientRect()
          const dx = (buttonRect.left + buttonRect.width / 2) - (windowRect.left + windowRect.width / 2)
          const dy = (buttonRect.top + buttonRect.height / 2) - (windowRect.top + windowRect.height / 2)
          const scale = Math.max(buttonRect.width / windowRect.width, 0.05)
          el.style.setProperty('--minimize-translate-x', `${Math.round(dx)}px`)
          el.style.setProperty('--minimize-translate-y', `${Math.round(dy)}px`)
          el.style.setProperty('--minimize-scale', scale.toFixed(3))
        } else {
          // Fallback: animate toward bottom-center (taskbar area)
          const windowRect = el.getBoundingClientRect()
          const dx = (window.innerWidth / 2) - (windowRect.left + windowRect.width / 2)
          const dy = window.innerHeight - (windowRect.top + windowRect.height / 2)
          el.style.setProperty('--minimize-translate-x', `${Math.round(dx)}px`)
          el.style.setProperty('--minimize-translate-y', `${Math.round(dy)}px`)
          el.style.setProperty('--minimize-scale', '0.1')
        }
      }
    }

    if (wasMin && !isMin) {
      // RESTORE: start restoring animation from minimized position
      setIsRestoring(true)
      const duration = windowEnterTiming.duration
      restoringTimeoutRef.current = window.setTimeout(() => {
        setIsRestoring(false)
        // Clean up minimize CSS properties
        const el = winRef.current
        if (el) {
          el.style.removeProperty('--minimize-translate-x')
          el.style.removeProperty('--minimize-translate-y')
          el.style.removeProperty('--minimize-scale')
        }
      }, duration)
    }
  }, [isMin, appId, windowEnterTiming.duration])

  // Cleanup restoring timeout
  useEffect(() => {
    return () => {
      if (restoringTimeoutRef.current !== null) {
        window.clearTimeout(restoringTimeoutRef.current)
      }
    }
  }, [])

  // Take DOM focus when this window becomes the active one — unless the very
  // click that activated it already put focus on a field inside it. Focusing
  // the root then blurs that field: the first click into cdbChat's composer
  // on a not-yet-active window lost its focus a moment later (#792).
  useEffect(() => {
    if (
      isActive &&
      !isMin &&
      !isClosing &&
      shouldClaimWindowFocus(winRef.current, document.activeElement)
    ) {
      winRef.current?.focus({ preventScroll: true })
    }
  }, [isActive, isMin])

  const applyWindowGeometry = useCallback(
    (
      nextPos: Coordinates,
      nextSize: { w: number; h: number },
      options?: { snap?: 'left' | 'right' | null; fromUser?: boolean },
    ) => {
      const el = winRef.current
      const prevPos = posRef.current
      const prevSize = sizeRef.current
      const positionChanged = prevPos.x !== nextPos.x || prevPos.y !== nextPos.y
      const sizeChanged = prevSize.w !== nextSize.w || prevSize.h !== nextSize.h
      let targetSnap = snappedSideRef.current

      if (options?.snap !== undefined && options.snap !== targetSnap) {
        setSnappedSide(options.snap)
        snappedSideRef.current = options.snap
        targetSnap = options.snap
      }

      if (el && (positionChanged || sizeChanged)) {
        el.style.left = `${nextPos.x}px`
        el.style.top = `${nextPos.y}px`
        el.style.width = `${nextSize.w}px`
        el.style.height = `${nextSize.h}px`
      }

      if (positionChanged) {
        posRef.current = nextPos
        setPos(nextPos)
      } else {
        posRef.current = { ...nextPos }
      }

      if (sizeChanged) {
        sizeRef.current = nextSize
        setSize(nextSize)
      } else {
        sizeRef.current = { ...nextSize }
      }

      prevRectRef.current = { pos: posRef.current, size: sizeRef.current }

      if (positionChanged || sizeChanged || options?.snap !== undefined) {
        onPositionChange?.(posRef.current, {
          fromUser: options?.fromUser ?? false,
          size: sizeRef.current,
          snap: targetSnap,
        })
      }
    },
    [onPositionChange],
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleResize = () => {
      const vw = window.innerWidth
      const vh = window.innerHeight
      const availableHeight = Math.max(vh - TASKBAR_HEIGHT, MIN_WINDOW_HEIGHT)
      viewportRef.current = { width: vw, height: vh }

      if (maximized) {
        applyWindowGeometry({ x: 0, y: 0 }, { w: vw, h: availableHeight }, { snap: null })
        return
      }

      const snapSide = snappedSideRef.current
      if (snapSide) {
        const snapWidth = clamp(Math.round(vw / 2), MIN_WINDOW_WIDTH, vw)
        const nextPos = { x: snapSide === 'left' ? 0 : Math.max(0, vw - snapWidth), y: 0 }
        applyWindowGeometry(nextPos, { w: snapWidth, h: availableHeight }, { snap: snapSide })
        return
      }

      const nextWidth = clamp(sizeRef.current.w, MIN_WINDOW_WIDTH, vw)
      const nextHeight = clamp(sizeRef.current.h, MIN_WINDOW_HEIGHT, availableHeight)
      const maxX = Math.max(0, vw - nextWidth)
      const maxY = Math.max(0, availableHeight - nextHeight)

      const nextPos = {
        x: clamp(posRef.current.x, 0, maxX),
        y: clamp(posRef.current.y, 0, maxY),
      }

      applyWindowGeometry(nextPos, { w: nextWidth, h: nextHeight })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [applyWindowGeometry, maximized])

  // Maximize / Restore with smooth geometry transition
  const applyGeometryTransition = useCallback((el: HTMLElement, callback: () => void) => {
    // Clear any pending transition cleanup
    if (geometryTransitionRef.current !== null) {
      window.clearTimeout(geometryTransitionRef.current)
      el.classList.remove('app-window--geometry-transition')
    }
    // Enable transition class, then apply geometry changes in next frame
    el.classList.add('app-window--geometry-transition')
    requestAnimationFrame(() => {
      callback()
      // Remove transition class after animation completes
      geometryTransitionRef.current = window.setTimeout(() => {
        el.classList.remove('app-window--geometry-transition')
        geometryTransitionRef.current = null
      }, windowEnterTiming.duration)
    })
  }, [windowEnterTiming.duration])

  const toggleMaximize = () => {
    if (!allowMaximize) return
    const el = winRef.current
    if (!maximized) {
      prevRectRef.current = { pos: posRef.current, size: sizeRef.current }
      const vw = window.innerWidth
      const vh = window.innerHeight
      posRef.current = { x: 0, y: 0 }
      sizeRef.current = { w: vw, h: vh - TASKBAR_HEIGHT }
      setPos(posRef.current)
      setSize(sizeRef.current)
      setSnappedSide(null)
      onPositionChange?.(posRef.current, {
        fromUser: false,
        size: sizeRef.current,
        snap: null,
        isMaximized: true,
      })
      if (el) {
        applyGeometryTransition(el, () => {
          el.style.left = `0px`
          el.style.top = `0px`
          el.style.width = `${vw}px`
          el.style.height = `${vh - TASKBAR_HEIGHT}px`
        })
      }
      setMaximized(true)
    } else {
      const prev = prevRectRef.current
      // If no previous rect exists (e.g. window was restored from persisted maximized state),
      // fall back to default opening geometry instead of the persisted maximized geometry.
      const s = prev?.size ?? createDefaultSize()
      const p = prev?.pos ?? calculateCenteredWindowPosition(s.w, s.h)
      posRef.current = p
      sizeRef.current = s
      setPos(p)
      setSize(s)
      setSnappedSide(null)
      onPositionChange?.(p, {
        fromUser: false,
        size: s,
        snap: null,
        isMaximized: false,
      })
      if (el) {
        applyGeometryTransition(el, () => {
          el.style.left = `${p.x}px`
          el.style.top = `${p.y}px`
          el.style.width = `${s.w}px`
          el.style.height = `${s.h}px`
        })
      }
      setMaximized(false)
    }
  }

  // Dragging
  const onTitleMouseDown = (e: React.MouseEvent) => {
    onFocus?.()
    // WICHTIG: Kein Restore bei einfachem Klick. Erst wenn wirklich gezogen wird (Schwellwert), wird restored.
    restoredOnDragRef.current = false
    deferredMaximizedRef.current = false
    snapPreviewRef.current = null
    setSnapPreview(null)
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y }
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onMouseMoveDrag)
    window.addEventListener('mouseup', onMouseUpDrag)
  }
  const onMouseMoveDrag = useCallback((e: MouseEvent) => {
    const d = dragRef.current
    if (!d) return
    const dx = e.clientX - d.startX
    const dy = e.clientY - d.startY
    // Wenn maximiert: Erst beim Ueberschreiten eines Schwellwerts wiederherstellen.
    if (maximized && !restoredOnDragRef.current) {
      const threshold = 5
      if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) {
        return
      }
      const prev = prevRectRef.current
      const s = prev?.size ?? createDefaultSize()
      const vw = window.innerWidth
      const vh = window.innerHeight
      const ratioX = vw > 0 ? clamp(e.clientX / vw, 0, 1) : 0.5
      const desiredX = e.clientX - s.w * ratioX
      const desiredY = e.clientY - 18 // approx half of the titlebar height
      const maxX = Math.max(0, vw - s.w)
      const maxY = Math.max(0, vh - s.h - TASKBAR_HEIGHT)
      const nextX = clamp(desiredX, 0, maxX)
      const nextY = clamp(desiredY, 0, maxY)
      const p = { x: nextX, y: nextY }

      posRef.current = p
      sizeRef.current = s
      prevRectRef.current = { pos: p, size: s }
      // UI nur via DOM aktualisieren - State-Updates erst bei mouseup
      const el0 = winRef.current
      if (el0) {
        el0.style.left = `${p.x}px`
        el0.style.top = `${p.y}px`
        el0.style.width = `${s.w}px`
        el0.style.height = `${s.h}px`
      }
      snappedSideRef.current = null
      deferredMaximizedRef.current = true
      // Drag-Start neu setzen ab aktueller Mausposition
      dragRef.current = { startX: e.clientX, startY: e.clientY, origX: p.x, origY: p.y }
      restoredOnDragRef.current = true
      return
    }
    if (!maximized && snappedSideRef.current && !restoredOnDragRef.current) {
      const threshold = 5
      if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) {
        return
      }
      const prev = prevRectRef.current
      const s = prev?.size ?? createDefaultSize()
      const dragState = dragRef.current
      const offsetX = dragState ? dragState.startX - dragState.origX : 0
      const offsetY = dragState ? dragState.startY - dragState.origY : 0
      const vw = window.innerWidth
      const vh = window.innerHeight
      const maxX = Math.max(0, vw - s.w)
      const maxY = Math.max(0, vh - s.h - TASKBAR_HEIGHT)
      const nextX = clamp(e.clientX - offsetX, 0, maxX)
      const nextY = clamp(e.clientY - offsetY, 0, maxY)
      posRef.current = { x: nextX, y: nextY }
      sizeRef.current = s
      const el0 = winRef.current
      if (el0) {
        el0.style.left = `${nextX}px`
        el0.style.top = `${nextY}px`
        el0.style.width = `${s.w}px`
        el0.style.height = `${s.h}px`
      }
      // State-Updates erst bei mouseup - hier nur Refs + DOM
      snappedSideRef.current = null
      prevRectRef.current = { pos: posRef.current, size: s }
      dragRef.current = { startX: e.clientX, startY: e.clientY, origX: nextX, origY: nextY }
      restoredOnDragRef.current = true
      return
    }
    const vw = window.innerWidth
    const vh = window.innerHeight
    const currentSize = sizeRef.current
    const maxX = Math.max(0, vw - currentSize.w)
    const maxY = Math.max(0, vh - currentSize.h - TASKBAR_HEIGHT)
    const nx = Math.min(Math.max(0, d.origX + dx), maxX)
    const ny = Math.min(Math.max(0, d.origY + dy), maxY)
    posRef.current = { x: nx, y: ny }
    const el = winRef.current
    if (el) {
      el.style.left = `${nx}px`
      el.style.top = `${ny}px`
    }

    // Visuelles Snap-Preview bei Randnaehe - nur bei Wertaenderung State updaten
    const EDGE_THRESHOLD = 24
    let nextPreview: null | 'left' | 'right' = null
    if (e.clientX <= EDGE_THRESHOLD) nextPreview = 'left'
    else if (e.clientX >= vw - EDGE_THRESHOLD) nextPreview = 'right'
    if (nextPreview !== snapPreviewRef.current) {
      snapPreviewRef.current = nextPreview
      setSnapPreview(nextPreview)
    }
  }, [maximized, initialPosition])
  const onMouseUpDrag = useCallback((e: MouseEvent) => {
    // Deferred state-updates nachholen die waehrend des Drags uebersprungen wurden
    const wasRestoredDuringDrag = restoredOnDragRef.current
    if (wasRestoredDuringDrag) {
      // Maximized/Snapped-State wurde waehrend des Drags nur per Ref geaendert,
      // jetzt React-State synchronisieren
      if (deferredMaximizedRef.current) {
        setMaximized(false)
        deferredMaximizedRef.current = false
      }
      setSnappedSide(null)
      setSize(sizeRef.current)
    }

    // Edge-Snap: split-left / split-right
    const EDGE_THRESHOLD = 24
    const vw = window.innerWidth
    const vh = window.innerHeight
    const mx = e.clientX

    // Falls an den linken oder rechten Rand gezogen, Fenster halbieren
    if (mx <= EDGE_THRESHOLD || mx >= vw - EDGE_THRESHOLD) {
      // Vorherige Position/Groesse merken (fuer potenzielles Restore in Zukunft)
      if (!snappedSideRef.current) {
        prevRectRef.current = { pos: posRef.current, size: sizeRef.current }
      }

      const halfW = Math.floor(vw / 2)
      const newSize = { w: halfW, h: vh - TASKBAR_HEIGHT }
      const newPos = { x: mx <= EDGE_THRESHOLD ? 0 : vw - halfW, y: 0 }
      const newSnapSide: 'left' | 'right' = mx <= EDGE_THRESHOLD ? 'left' : 'right'

      posRef.current = newPos
      sizeRef.current = newSize
      setPos(newPos)
      setSize(newSize)
      setSnappedSide(newSnapSide)
      snappedSideRef.current = newSnapSide
      onPositionChange?.(newPos, {
        fromUser: true,
        size: newSize,
        snap: newSnapSide,
        isMaximized: false,
      })

      const el = winRef.current
      if (el) {
        applyGeometryTransition(el, () => {
          el.style.left = `${newPos.x}px`
          el.style.top = `${newPos.y}px`
          el.style.width = `${newSize.w}px`
          el.style.height = `${newSize.h}px`
        })
      }
    } else {
      // Normales Loslassen: aktuelle berechnete Position uebernehmen
      if (snappedSideRef.current) {
        const prev = prevRectRef.current
        const p = prev?.pos ?? createInitialPosition()
        const s = prev?.size ?? createDefaultSize()
        posRef.current = p
        sizeRef.current = s
        setPos(p)
        setSize(s)
        setSnappedSide(null)
        snappedSideRef.current = null
        onPositionChange?.(p, {
          fromUser: true,
          size: s,
          snap: null,
          isMaximized: false,
        })
        const el = winRef.current
        if (el) {
          applyGeometryTransition(el, () => {
            el.style.left = `${p.x}px`
            el.style.top = `${p.y}px`
            el.style.width = `${s.w}px`
            el.style.height = `${s.h}px`
          })
        }
      } else {
        setPos(posRef.current)
        onPositionChange?.(posRef.current, {
          fromUser: true,
          size: sizeRef.current,
          snap: null,
          isMaximized: false,
        })
      }
    }

    // Preview ausblenden
    snapPreviewRef.current = null
    setSnapPreview(null)
    dragRef.current = null
    document.body.style.userSelect = ''
    window.removeEventListener('mousemove', onMouseMoveDrag)
    window.removeEventListener('mouseup', onMouseUpDrag)
  }, [onMouseMoveDrag, onPositionChange, applyGeometryTransition])

  // Resizing
  const startResize = (direction: ResizeDirection, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onFocus?.()
    if (maximized) {
      const prev = prevRectRef.current
      const p = prev?.pos ?? createInitialPosition()
      const s = prev?.size ?? createDefaultSize()
      posRef.current = p
      sizeRef.current = s
      setPos(p)
      setSize(s)
      setSnappedSide(null)
      snappedSideRef.current = null
      onPositionChange?.(p, {
        fromUser: false,
        size: s,
        snap: null,
        isMaximized: false,
      })
      const el = winRef.current
      if (el) {
        el.style.left = `${p.x}px`
        el.style.top = `${p.y}px`
        el.style.width = `${s.w}px`
        el.style.height = `${s.h}px`
      }
      setMaximized(false)
    }

    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origW: sizeRef.current.w,
      origH: sizeRef.current.h,
      origX: posRef.current.x,
      origY: posRef.current.y,
      direction,
      snapSide: snappedSideRef.current,
    }
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onMouseMoveResize)
    window.addEventListener('mouseup', onMouseUpResize)
  }
  const onMouseMoveResize = useCallback((e: MouseEvent) => {
    const r = resizeRef.current
    if (!r) return
    const dx = e.clientX - r.startX
    const dy = e.clientY - r.startY
    const vw = window.innerWidth
    const vh = window.innerHeight
    const minW = MIN_WINDOW_WIDTH
    const minH = MIN_WINDOW_HEIGHT

    let nextX = r.origX
    let nextY = r.origY
    let nextW = r.origW
    let nextH = r.origH

    if (r.direction.includes('w')) {
      const maxX = r.origX + r.origW - minW
      nextX = clamp(r.origX + dx, 0, maxX)
      const availableWidth = Math.max(minW, vw - nextX)
      nextW = Math.min(Math.max(minW, r.origW + (r.origX - nextX)), availableWidth)
    }

    if (r.direction.includes('e')) {
      const availableWidth = Math.max(minW, vw - nextX)
      nextW = Math.min(Math.max(minW, r.origW + dx), availableWidth)
    }

    if (r.direction.includes('n')) {
      const maxY = r.origY + r.origH - minH
      nextY = clamp(r.origY + dy, 0, maxY)
      const availableHeight = Math.max(minH, vh - TASKBAR_HEIGHT - nextY)
      nextH = Math.min(Math.max(minH, r.origH + (r.origY - nextY)), availableHeight)
    }

    if (r.direction.includes('s')) {
      const availableHeight = Math.max(minH, vh - TASKBAR_HEIGHT - nextY)
      nextH = Math.min(Math.max(minH, r.origH + dy), availableHeight)
    }

    if (r.snapSide === 'left') {
      nextX = 0
      nextY = 0
      const maxHeight = Math.max(minH, vh - TASKBAR_HEIGHT)
      nextW = Math.min(Math.max(minW, nextW), vw)
      nextH = Math.min(Math.max(minH, nextH), maxHeight)
    } else if (r.snapSide === 'right') {
      const maxHeight = Math.max(minH, vh - TASKBAR_HEIGHT)
      nextW = Math.min(Math.max(minW, nextW), vw)
      nextX = Math.max(0, vw - nextW)
      nextY = 0
      nextH = Math.min(Math.max(minH, nextH), maxHeight)
    }

    posRef.current = { x: nextX, y: nextY }
    sizeRef.current = { w: nextW, h: nextH }

    const el = winRef.current
    if (el) {
      el.style.left = `${nextX}px`
      el.style.top = `${nextY}px`
      el.style.width = `${nextW}px`
      el.style.height = `${nextH}px`
    }
  }, [])
  const onMouseUpResize = useCallback(() => {
    if (resizeRef.current) {
      const finalPos = posRef.current
      const finalSize = sizeRef.current
      const finalSnapSide = resizeRef.current.snapSide

      setPos(finalPos)
      setSize(finalSize)
      setSnappedSide(finalSnapSide)
      snappedSideRef.current = finalSnapSide
      prevRectRef.current = { pos: finalPos, size: finalSize }

      onPositionChange?.(finalPos, {
        fromUser: true,
        size: finalSize,
        snap: finalSnapSide,
        isMaximized: false,
      })
    }
    resizeRef.current = null
    document.body.style.userSelect = ''
    window.removeEventListener('mousemove', onMouseMoveResize)
    window.removeEventListener('mouseup', onMouseUpResize)
  }, [onPositionChange])

  // Groesse bei Window-Resize aktualisieren, falls maximiert
  useEffect(() => {
    if (!maximized) return
    const onResize = () => {
      const vw = window.innerWidth
      const vh = window.innerHeight
      sizeRef.current = { w: vw, h: vh - TASKBAR_HEIGHT }
      setSize(sizeRef.current)
      const el = winRef.current
      if (el) {
        el.style.left = `0px`
        el.style.top = `0px`
        el.style.width = `${vw}px`
        el.style.height = `${vh - TASKBAR_HEIGHT}px`
      }
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [maximized])

  // Nach allen Hooks: bei minimiert/geschlossen nichts rendern

  // Snap-Preview Overlay (Portal)
  const previewOverlay = renderedSnapSide
    ? createPortal(
        <div
          className={cn(
            'snap-overlay',
            snapOverlayVisible ? 'snap-overlay--visible' : 'snap-overlay--hidden'
          )}
        >
          <div className="snap-overlay__scrim" />
          <div
            className="snap-overlay-area"
            style={{
              top: 8,
              left: renderedSnapSide === 'left' ? 8 : 'auto',
              right: renderedSnapSide === 'right' ? 8 : 'auto',
              width: 'calc(50% - 16px)',
              height: `calc(100vh - ${TASKBAR_HEIGHT}px - 16px)`,
            }}
          />
        </div>,
        document.body,
      )
    : null

  return (
    <>
      {previewOverlay}
      <div
        ref={winRef}
        className={cn(
          'app-window fixed select-none focus:outline-none',
          maximized && 'maximized',
          isClosing ? 'app-window--closing' : isMin ? 'app-window--minimized' : isRestoring ? 'app-window--restoring' : 'app-window--restored',
          isMounted ? 'window-mounted' : 'window-enter',
          isActive ? 'app-window--active' : 'app-window--inactive',
          className,
        )}
        style={{
          ...animationStyle,
          left: pos.x,
          top: pos.y,
          width: size.w,
          height: size.h,
          zIndex,
        }}
        inert={isMin ? true : undefined}
        aria-hidden={isMin}
        onMouseDown={(event) => {
          if (isMin || isClosing) return
          onFocus?.()
          // React events from PORTALED children (Radix dropdowns render into
          // document.body) bubble through the React tree to this handler even
          // though their DOM lives outside the window element. Stealing DOM
          // focus from them dismisses non-modal menus mid-click (focus-outside
          // dismiss) — the item unmounts between mousedown and mouseup, the
          // browser never emits a click, and the selection silently dies
          // (#324). Only take focus for clicks physically inside the window,
          // and not from an editor that focused itself during this mousedown
          // (Monaco, #1156).
          if (shouldFocusWindowOnMouseDown(winRef.current, event.target, document.activeElement)) {
            winRef.current?.focus({ preventScroll: true })
          }
        }}
        onFocusCapture={(event) => {
          if (!isMin && !isClosing && shouldRaiseWindowOnFocus(winRef.current, event.target)) onFocus?.()
        }}
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        data-active={isActive ? 'true' : 'false'}
        data-snap={snappedSide ?? 'none'}
        data-window-id={appId}
        tabIndex={isMin ? -1 : 0}
      >
        <div
          className="window-titlebar"
          onMouseDown={onTitleMouseDown}
          onDoubleClick={toggleMaximize}
        >
          <div className="window-title" id={titleId}>
            {icon ? (
              <div className="w-4 h-4 flex items-center justify-center">
                {icon}
              </div>
            ) : (
              <HelpCircle className="w-4 h-4" aria-hidden />
            )}
            {title}
          </div>
          <div className="window-controls">
            {allowMinimize && (
              <button
                type="button"
                className="control minimize"
                aria-label={isMin ? t('window.restore') : t('window.minimize')}
                onClick={(e) => {
                  // Remove focus before minimizing to prevent accessibility warning
                  e.currentTarget.blur()
                  if (onToggleMinimize) onToggleMinimize()
                  else setMinState((v) => !v)
                }}
              >
                <Minus className="h-4 w-4" />
              </button>
            )}
            {allowMaximize && (
              <button
                type="button"
                className="control maximize"
                aria-label={maximized ? t('window.restore') : t('window.maximize')}
                onClick={toggleMaximize}
              >
                {maximized ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </button>
            )}
            {allowClose && (
              <button
                type="button"
                className="control close"
                aria-label={t('window.close')}
                onClick={(e) => {
                  // Remove focus before closing to prevent accessibility warning
                  e.currentTarget.blur()
                  requestClose()
                }}
              >
                <CloseIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <div className="window-content p-4 select-text">
          {children}
        </div>
        {RESIZE_HANDLES.map(({ direction, className: handleClass, labelKey }) => (
          <div
            key={direction}
            className={cn('resize-handle', handleClass)}
            onMouseDown={(event) => startResize(direction, event)}
            aria-label={t(labelKey)}
            title={t(labelKey)}
            role="presentation"
          />
        ))}
      </div>
    </>
  )
}

// Export memoized version for better performance
export default memo(AppWindow)
